// 對話框行為：開啟時把焦點移進去並限制在內（Tab 循環）、Esc 關閉、鎖住背景捲動；
// 關閉時把焦點還給開啟前的元素（例如清單中的卡片），讓鍵盤使用者不會迷路。
// 支援疊加（例如詳情裡再開截圖燈箱）：只有最上層的對話框處理 Esc 與 Tab。
import { type RefObject, useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, iframe, [tabindex]:not([tabindex="-1"])';

/** 目前開啟中的對話框，最後一個在最上層 */
const stack: object[] = [];

export function useDialog(ref: RefObject<HTMLElement | null>, open: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const token = {};
    stack.push(token);
    const previous = document.activeElement as HTMLElement | null;
    const root = ref.current;
    // 先找標記 data-autofocus 的元素（關閉按鈕），沒有才聚焦對話框本身
    const initial = root?.querySelector<HTMLElement>('[data-autofocus]') ?? root;
    initial?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (stack.at(-1) !== token) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !root) return;
      const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    const padding = document.body.style.paddingRight;
    // 鎖住捲動時捲軸消失：補上捲軸原本的寬度，頁面不左右跳動、也不因寬度改變重排（GDO-35）。
    // 捲軸不佔空間（手機、macOS 的浮動捲軸）時寬度差是 0，不補
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    if (gutter > 0 && document.body.style.overflow !== 'hidden') document.body.style.paddingRight = `${gutter}px`;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      stack.splice(stack.indexOf(token), 1);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = padding;
      // 開啟前的元素可能已被虛擬清單回收；仍在頁面上才還原焦點
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open, ref]);
}
